// Web counterpart to TonyteMapView.tsx. MapScreen.tsx and
// StudiZoneMapScreen.tsx each build a fully self-contained Leaflet HTML
// page (CARTO tiles, markers, the works) and hand it to a WebView via
// `source={{ html }}` — on web there's no need for any WebView/native
// wrapper at all: the exact same HTML can be hosted directly in the
// browser DOM via an <iframe srcDoc>, which is a real page element, not a
// nested native browser view.
//
// This is a drop-in replacement for react-native-webview's <WebView> —
// same ref shape (`injectJavaScript`), same `onMessage` event shape
// (`{ nativeEvent: { data } }`) — so neither screen's own map/marker logic
// needed to change, only the one `import { WebView } from
// 'react-native-webview'` line (now `TonyteMapView`) in each.
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { StyleSheet, type StyleProp, type ViewStyle } from 'react-native';

export type TonyteMapViewHandle = {
  injectJavaScript: (script: string) => void;
};

// Same shape as TonyteMapView.tsx's export of the same name — kept in both
// files so MapScreen.tsx/StudiZoneMapScreen.tsx can import one name
// (`TonyteMapViewRef`) regardless of which platform file actually resolves.
export type TonyteMapViewRef = TonyteMapViewHandle;

type TonyteMapViewSource = { html: string; baseUrl?: string };
type TonyteMapViewMessageEvent = { nativeEvent: { data: string } };

// Deliberately not typed with a catch-all index signature: both screens
// also pass a handful of react-native-webview's native-only props
// (originWhitelist, javaScriptEnabled, domStorageEnabled, androidLayerType,
// mixedContentMode, setSupportMultipleWindows,
// showsHorizontalScrollIndicator, showsVerticalScrollIndicator, bounces) —
// every one of them is either a no-op or already-true-by-default in a real
// browser, so they're simply not destructured below rather than declared
// here (an index signature alongside named optional properties makes
// TypeScript widen the named ones to `unknown`, which isn't worth it just
// to type-accept props this component never reads).
type TonyteMapViewProps = {
  style?: StyleProp<ViewStyle>;
  source: TonyteMapViewSource;
  onMessage?: (event: TonyteMapViewMessageEvent) => void;
  onError?: (event: { nativeEvent: unknown }) => void;
  onHttpError?: (event: { nativeEvent: unknown }) => void;
  // Declared-but-unused react-native-webview native-only props both
  // screens still pass — every one is a no-op or already-true-by-default
  // in a real browser. Listed individually (not a `[key: string]: unknown`
  // index signature) because that combination makes TypeScript widen the
  // named properties above to `unknown` too.
  originWhitelist?: string[];
  javaScriptEnabled?: boolean;
  domStorageEnabled?: boolean;
  androidLayerType?: string;
  mixedContentMode?: string;
  setSupportMultipleWindows?: boolean;
  showsHorizontalScrollIndicator?: boolean;
  showsVerticalScrollIndicator?: boolean;
  bounces?: boolean;
};

// The HTML both screens generate calls
// `window.ReactNativeWebView && window.ReactNativeWebView.postMessage(...)`
// to talk back to RN — that code is shared/native-facing and untouched.
// This shim makes the exact same call work verbatim inside a plain browser
// iframe by forwarding to the standard cross-frame postMessage API.
function shimReactNativeWebViewBridge(html: string): string {
  return `<script>window.ReactNativeWebView={postMessage:function(data){window.parent.postMessage(data,"*");}};</script>${html}`;
}

export const TonyteMapView = forwardRef<TonyteMapViewHandle, TonyteMapViewProps>(
  ({ style, source, onMessage, onError }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        injectJavaScript: (script: string) => {
          // Same-origin `srcDoc` iframe (no `sandbox` attribute) — the
          // parent can execute code in its context directly, the exact
          // same capability injectJavaScript gives on native.
          try {
            (iframeRef.current?.contentWindow as (Window & { eval?: (code: string) => unknown }) | null)
              ?.eval?.(script);
          } catch (error) {
            if (__DEV__) console.warn('[TonyteMapView.web] injectJavaScript failed', error);
          }
        },
      }),
      [],
    );

    useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
        if (event.source !== iframeRef.current?.contentWindow) return;
        const data = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
        onMessage?.({ nativeEvent: { data } });
      };
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }, [onMessage]);

    const shimmedHtml = useMemo(() => shimReactNativeWebViewBridge(source.html), [source.html]);
    const flatStyle = (StyleSheet.flatten(style) || {}) as Record<string, unknown>;

    return (
      <iframe
        ref={iframeRef}
        srcDoc={shimmedHtml}
        title="Tonyte map"
        // `<iframe>` is a "replaced element" (like <img>/<video>) with a
        // browser-default intrinsic size of 300x150 — unlike a plain <div>,
        // `position: absolute` + inset (top/left/right/bottom: 0) alone does
        // NOT stretch it to fill its container; explicit width/height: 100%
        // is required too. Confirmed live: without this the map rendered
        // pinned to a literal 300x150 box instead of filling the screen.
        // These come first so `flatStyle` (StyleSheet.absoluteFill, from
        // MapScreen.tsx/StudiZoneMapScreen.tsx, unchanged) can still win if
        // it ever sets its own explicit width/height.
        style={{ border: 'none', width: '100%', height: '100%', ...flatStyle }}
        onError={() => onError?.({ nativeEvent: {} })}
      />
    );
  },
);

export default TonyteMapView;
